import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { collection, CollectionReference } from "firebase/firestore";
import { UsedBlueprint } from "../App";
import { logDebug } from "../LocalLogger";
import { fixProduct, OptionValue, ProductType, ProductVariant } from "./productTypes";
import { PreviewImage } from "../UI/ClickableImage";
import { appStore } from "../utils/AppMobx";
import { globalInitDone } from "../utils/AppInit";
import printifyService from "../utils/PrintifyService";

export const ProductsCreation: React.FC = observer(() => {
	const [prices, setPrices] = React.useState<Record<number, number>>({});
	const [title, setTitle] = React.useState<string>("");
	const [description, setDescription] = React.useState<string>("");
	const [categories, setCategories] = React.useState<string[]>([]);
	const [selectedFilters, setSelectedFilters] = React.useState<{ type: string; id: number }[]>([]);
	const [checkedBlueprint, setCheckedBlueprint] = React.useState<number>(-1);
	const [blueprintData, setBlueprintData] = React.useState<Record<string, ProductType>>({});
	const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
	const firestore = useFirestore();
	const useInAppBlueprintsRef = collection(firestore, "/store/site/useInAppBlueprints") as CollectionReference<UsedBlueprint>;
	const useInAppBlueprintsCD = useFirestoreCollectionData<UsedBlueprint>(useInAppBlueprintsRef, { idField: "useId" });

	function getFilteredBlueprints(variants: ProductVariant[], removeDuplicates = true): ProductVariant[] {
		let filteredVariants: ProductVariant[] = [];
		variants.forEach((variant) => {
			if (!variant.image) return; // Skip variants without images
			if (!variant.image) {
				return null; // Skip variants without images
			}
			const onlyColorFilters = selectedFilters.filter((f) => f.type === "color");
			//@ts-ignore
			const onlyColorOptions = variant.options.filter((opt) => opt.type === "color");
			//@ts-ignore
			if (!onlyColorFilters.find((f) => f.id === onlyColorOptions[0].id)) {
				return null; // Skip if no matching color filter
			}
			filteredVariants.push(variant);
		});

		if (removeDuplicates) {
			// remove duplicates based on image
			filteredVariants = filteredVariants.filter(
				(variant, index, self) => index === self.findIndex((v) => v.image === variant.image)
			);
		}
		return filteredVariants;
	}

	if (useInAppBlueprintsCD.status === "loading" || !globalInitDone()) {
		return <div>Loading blueprints...</div>;
	}

	return (
		<div className="p-4">
			<h1 className="text-3xl m-2">Products Creation Page</h1>
			<div className="flex gap-4 overflow-x-auto">
				{appStore.allImages.map((image) => (
					<div
						key={image.id}
						onClick={() => {
							setCategories([]);
							setSelectedFilters([]);
							setBlueprintData({});
							setTitle("");
							setDescription("");
							setCheckedBlueprint(-1);
							setSelectedImage(image.id);
							logDebug("Selected image:", image.file_name);
						}}
						className={`flex flex-col items-center cursor-pointer ${selectedImage === image.id ? "bg-sky-200" : ""}`}
					>
						<div className="flex items-center justify-center gap-2 mb-2">
							<p className="text-sm max-w-[90px] overflow-hidden overflow-ellipsis whitespace-nowrap">{image.file_name}</p>
						</div>
						<img src={image.preview_url} alt={image.file_name} style={{ minWidth: "100px", maxHeight: 200 }} />
						{/* <p>{image.file_name}</p> */}
					</div>
				))}
			</div>
			<ul>
				{useInAppBlueprintsCD.data.map((product) => {
					if (checkedBlueprint !== -1 && checkedBlueprint !== product.blueprintId) {
						return null; // Skip if not checked
					}
					const provider = appStore.onlyUKProviders.find((provider) => provider.id.toFixed() === product.providerId)!;
					const blueprint = provider.blueprints?.find((blueprint) => blueprint.id === product.blueprintId);
					return (
						<li key={product.blueprintId}>
							<input
								type="checkbox"
								checked={checkedBlueprint === product.blueprintId}
								onChange={(e) => {
									const newChecked = e.target.checked;
									if (newChecked) {
										setCheckedBlueprint(product.blueprintId);
									} else {
										setCheckedBlueprint(-1);
									}
								}}
							/>
							{product.title} - {blueprint?.brand || "Generic Brand"}
							<button
								disabled={!selectedImage}
								className="ml-2 bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50"
								onClick={async () => {
									if (!selectedImage) {
										alert("Please select an image first.");
										return;
									}
									if (blueprintData[product.blueprintId]) {
										// don't fetch again if already fetched
										logDebug("Blueprint data already fetched for:", product.blueprintId);
										alert("Blueprint data already fetched for this product.");
										return;
									}
									let productData = await printifyService.createDummyProductAndFetchPrices(
										provider.id,
										product.blueprintId,
										selectedImage!
									);
									logDebug("Product data fetched pre fix:", productData);
									productData = await fixProduct(productData);

									logDebug("Product data fetched:", productData);
									setBlueprintData((prev) => ({
										...prev,
										[product.blueprintId]: productData,
									}));
								}}
							>
								Fetch Prices
							</button>
							{blueprintData[product.blueprintId] && checkedBlueprint === product.blueprintId && (
								<div className="mt-2">
									<h3>Product Meta:</h3>

									<div className="flex flex-col gap-2">
										<div>Title:</div>
										<input
											type="text"
											className="border p-1 rounded mb-2 w-[400px]"
											placeholder="Product Title"
											value={title}
											onChange={(e) => setTitle(e.target.value)}
										/>
										<div>Description:</div>
										<textarea
											className="border p-1 rounded mb-2 w-[400px]"
											placeholder="Product Description"
											value={description}
											onChange={(e) => setDescription(e.target.value)}
										></textarea>
									</div>
									<h3>Product Categories:</h3>
									{categories.map((category) => (
										<span
											key={category}
											className="bg-gray-200 px-2 py-1 rounded mr-2 mb-2 inline-block cursor-pointer"
											onClick={() => {
												setCategories((prev) => prev.filter((c) => c !== category));
											}}
										>
											{category} <span className="text-red-500">x</span>
										</span>
									))}
									<input
										type="text"
										className="border p-1 rounded mb-2"
										placeholder="Add category (Enter)"
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												const newCategory = (e.target as HTMLInputElement).value.trim();
												if (newCategory && !categories.includes(newCategory)) {
													setCategories((prev) => [...prev, newCategory]);
													(e.target as HTMLInputElement).value = ""; // Clear input
												}
											}
										}}
									/>
									<h3>Product Filters:</h3>
									{blueprintData[product.blueprintId].options?.map((option) => {
										logDebug("Option:", blueprintData[product.blueprintId].options);
										return (
											<div key={option.type} className="mb-2">
												<h4 className="font-semibold">{option.name}</h4>
												<div className="flex gap-1">
													{option.values
														.filter((v) => v.used)
														.map((value) => (
															<div
																key={value.id}
																className={`border p-1 rounded cursor-pointer ${
																	selectedFilters.find((v) => v.id === value.id) ? "bg-red-500" : ""
																}`}
																onClick={() => {
																	setSelectedFilters((prev) => {
																		if (prev.find((v) => v.id === value.id)) {
																			return prev.filter((v) => v.id !== value.id);
																		} else {
																			return [
																				...prev,
																				{
																					id: value.id,
																					type: option.type,
																				},
																			];
																		}
																	});
																}}
															>
																{value.type === "color" ? (
																	<div
																		className={`border w-6 h-6`}
																		style={{
																			backgroundColor: value.colors![0],
																		}}
																	></div>
																) : (
																	<div className="border min-w-6 h-6 text-center">{value.title}</div>
																)}
															</div>
														))}
												</div>
											</div>
										);
									}) || <p>No options available for this product.</p>}
									{blueprintData[product.blueprintId].per_cost?.map((variant) => (
										<div key={variant.id} className="mb-2">
											<h4 className="font-semibold">{variant.title}</h4>
											{/* <p>Price: ${variant.price / 100}</p> */}
											<p>Cost: ${variant.cost / 100}</p>
											<input
												type="text"
												className="border p-1 rounded"
												placeholder="price"
												value={prices[variant.cost] || ""}
												onChange={(e) => {
													const newPrice = parseFloat(e.target.value);
													if (!isNaN(newPrice)) {
														setPrices((prev) => ({
															...prev,
															[variant.cost]: newPrice,
														}));
													}
												}}
											/>
											{variant.image && <img src={variant.image} alt={variant.title} className="w-16 h-16" />}
										</div>
									)) || <p>No variants available for this product.</p>}
									<div className="flex gap-2">
										<div>Filtered Variants</div>
										<div className="flex flex-wrap gap-2">
											{getFilteredBlueprints(blueprintData[product.blueprintId].variants)?.map((variant) => {
												if (!variant.image) {
													return null; // Skip variants without images
												}
												const onlyColorFilters = selectedFilters.filter((f) => f.type === "color");
												//@ts-ignore
												const onlyColorOptions = variant.options.filter((opt) => opt.type === "color");
												//@ts-ignore
												if (!onlyColorFilters.find((f) => f.id === onlyColorOptions[0].id)) {
													return null; // Skip if no matching color filter
												}
												logDebug("selectedFilters:", onlyColorFilters, onlyColorOptions);
												if (variant.options)
													return (
														<div key={variant.id} className="mb-2">
															{variant.image && <PreviewImage imageUrl={variant.image} cls="w-64 h-64" />}
														</div>
													);
											}) || <p>No variants available for this product.</p>}
										</div>
									</div>
									<button
										className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
										onClick={() => {
											let failed = false;
											const filteredVariants = getFilteredBlueprints(
												blueprintData[product.blueprintId].variants,
												false
											);
											if (filteredVariants.length > 100) {
												alert("Too many variants selected ( max 100). Please refine your filters.");
												failed = true;
											}
											// check if all prices are set
											blueprintData[product.blueprintId].per_cost?.forEach((variant) => {
												if (!prices[variant.cost] || prices[variant.cost] <= variant.cost / 100) {
													alert(`Price not set for variant: ${variant.title} or price is too low.`);
													failed = true;
												}
											});
											if (failed) {
												return;
											}
											logDebug("Creating product with selected filters:", selectedFilters);
											// printifyService.createProductWithSelectedFilters(
											// 	product.blueprintId,
											// provider.id,
											// 	filteredVariants,
											// 	categories,
											// 	title,
											// 	description
											// prices
											// [{imageId: selectedImage!, position: "front"}]
											// );
										}}
									>
										Create Product
									</button>
									{/* <pre className="bg-gray-100 p-2 rounded">
										{JSON.stringify(blueprintData[product.blueprintId], null, 2)}
									</pre> */}
								</div>
							)}
						</li>
					);
				})}
			</ul>
			{/* <p>Total Bluprints: {useInAppBlueprintsCD.data.length}</p> */}
		</div>
	);
});
